import React, { useState, useMemo, useRef, useEffect } from 'react';


const Tooltip = ({ data, position }) => {
  const tipRef = useRef(null);
  const [mySize, setMySize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!tipRef.current) return;
    const { width, height } = tipRef.current.getBoundingClientRect();
    setMySize({ w: width, h: height });
  }, [data]); 

  const style = useMemo(() => {
    if (!data) return { display: 'none' };

    const margin = 10;
    const viewW = window.innerWidth;
    const viewH = window.innerHeight;

    let left = position.x + margin;
    let top  = position.y - mySize.h / 2;

    if (left + mySize.w > viewW - margin) {
      left = position.x - mySize.w - margin;
    }

    if (top + mySize.h > viewH - margin) {
      top = position.y - mySize.h - margin;
    }
    if (top < margin) {
      top = margin;
    }

    return {
      position: 'fixed',
      left: `${left}px`,
      top: `${top}px`,
      zIndex: 1000,
      pointerEvents: 'none',
    };
  }, [data, position, mySize]);

  if (!data) return null;

  const formatTime = (ts) =>
    ts ? new Date(ts * 1000).toLocaleString() : 'N/A';

  const formatDuration = (queue, end) => {
    if (!queue || !end) return 'N/A';
    const duration = end - queue;
    return `${duration.toFixed(2)}s`;
  };

  const getStatusColor = () => {
    if (data.expired) return 'border-red-500';
    if (!data.ready) return 'border-gray-400';
    return 'border-blue-500';
  };

  const getStatusText = () => {
    if (data.expired) return 'Expired';
    if (!data.ready) return 'Not Ready';
    if (data.start_time && data.end_time) return 'Completed';
    return 'Running';
  };

  return (
    <div
      ref={tipRef}
      className={`bg-white border-2 ${getStatusColor()} text-gray-800 p-4 rounded-lg shadow-xl w-full max-w-sm text-sm`}
      style={style}
    >
      <div className="space-y-2">
        
      <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-600">Task:</span>
          <span className="font-mono font-bold text-sm">{data.key}</span>
        </div>

        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-600">Name:</span>
          <span className="font-mono font-bold text-sm">{data.name || "-"}</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-600">Status:</span>
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            data.expired ? 'bg-red-100 text-red-800' :
            !data.ready ? 'bg-gray-100 text-gray-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {getStatusText()}
          </span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-600">Runtime:</span>
          <span>{data.runtime}s</span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-600">Queue Time:</span>
          <span className="text-xs">{formatTime(data.queue_time)}</span>
        </div>
        
        {data.start_time && (
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-600">Start Time:</span>
            <span className="text-xs">{formatTime(data.start_time)}</span>
          </div>
        )}
        
        {data.end_time && (
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-600">End Time:</span>
            <span className="text-xs">{formatTime(data.end_time)}</span>
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-600">Duration:</span>
          <span>{formatDuration(data.queue_time, data.end_time)}</span>
        </div>
        
        {data.monitors && data.monitors.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-200">
            <span className="font-semibold text-gray-600 block mb-1">Monitors:</span>
            <div className="text-xs text-gray-500 max-h-20 overflow-y-auto">
              {data.monitors.map((monitor, index) => (
                <div key={index} className="truncate" title={monitor}>
                  {monitor}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TaskBar = ({ taskKey, taskData, scale, onMouseEnter, onMouseLeave }) => {
  const { queue_time, start_time, end_time, ready, expired, history = [] } = taskData;
  
  if (!queue_time) return null;

  const queueStartPos = scale(queue_time);
  const executionStartPos = start_time ? scale(start_time) : null;
  const executionEndPos = end_time ? scale(end_time) : null;
  
  const barBaseStyle = "h-7 absolute";
  const historyBarBaseStyle = "h-7 absolute opacity-70"; 

  return (
    <div
      className="w-full h-full relative flex items-center"
      onMouseEnter={(e) => onMouseEnter({ ...taskData, key: taskKey }, { x: e.clientX, y: e.clientY })}
      onMouseLeave={onMouseLeave}
    >
      {history.map((hist, index) => {
        if (!hist.start_time || !hist.end_time) return null;

        const histStartPos = scale(hist.start_time);
        const histEndPos = scale(hist.end_time);
        const histWidth = Math.max(0, histEndPos - histStartPos);

        const histColor = hist.expired ? 'bg-red-300' : 'bg-gray-300';
        
        return (
          <div
            key={`${taskKey}-hist-${index}`}
            className={`${historyBarBaseStyle} ${histColor} rounded-md`}
            style={{
              left: `${histStartPos}%`,
              width: `${histWidth}%`,
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          />
        );
      })}

      {(() => {
        if (!start_time) {
          let color = ready ? 'bg-blue-300' : 'bg-gray-400';
          if (expired) color = 'bg-red-400';
          return <div className={`${barBaseStyle} ${color} rounded-md`} style={{ left: `${queueStartPos}%`, width: '10px' }} />;
        }
        const queueWidth = Math.max(0, executionStartPos - queueStartPos);
        const queueRoundedClass = 'rounded-l-lg';
        if (!end_time) {
          const runningColor = expired ? 'bg-red-400' : 'bg-blue-500';
          return (
            <>
              {queueWidth > 0 && <div className={`${barBaseStyle} bg-blue-300 ${queueRoundedClass}`} style={{ left: `${queueStartPos}%`, width: `${queueWidth}%` }} />}
              <div className={`${barBaseStyle} ${runningColor} rounded-r-lg animate-pulse`} style={{ left: `${executionStartPos}%`, width: '15px' }} />
            </>
          );
        }
        const executionWidth = Math.max(0, executionEndPos - executionStartPos);
        const executionRoundedClass = queueWidth > 0.1 ? 'rounded-r-lg' : 'rounded-lg';
        return (
          <>
            {queueWidth > 0 && <div className={`${barBaseStyle} bg-blue-300 ${queueRoundedClass}`} style={{ left: `${queueStartPos}%`, width: `${queueWidth}%` }} />}
            <div className={`${barBaseStyle} bg-blue-500 ${executionRoundedClass}`} style={{ left: `${executionStartPos}%`, width: `${executionWidth}%` }} />
          </>
        );
      })()}
    </div>
  );
};

export const HeapChart = ({ reportTrace, dependencyPairs = [] }) => {
  const [hoveredData, setHoveredData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [selectedTaskKey, setSelectedTaskKey] = useState(null);
  const chartRef = useRef(null);

  const processedData = useMemo(() => {
    if (!reportTrace) return [];
    return Object.entries(reportTrace).map(([key, value]) => ({ key, ...value }));
  }, [reportTrace]);

  const dependenciesMap = useMemo(() => {
    const map = new Map();
    for (const chain of dependencyPairs) {
      for (let i = 0; i < chain.length - 1; i++) {
        const dependent = chain[i];
        const dependency = chain[i + 1];
        if (!map.has(dependent)) map.set(dependent, []);
        map.get(dependent).push(dependency);
      }
    }
    return map;
  }, [dependencyPairs]);

  const highlightedDependencies = useMemo(() => {
    if (!selectedTaskKey) return new Set();

    const dependenciesToHighlight = new Set([selectedTaskKey]);
    const queue = [selectedTaskKey];

    while (queue.length > 0) {
        const currentKey = queue.shift();
        const directDependencies = dependenciesMap.get(currentKey) || [];

        for (const dependency of directDependencies) {
            if (!dependenciesToHighlight.has(dependency)) {
                dependenciesToHighlight.add(dependency);
                queue.push(dependency);
            }
        }
    }
    return dependenciesToHighlight;
  }, [selectedTaskKey, dependenciesMap]);

  const { minTime, maxTime } = useMemo(() => {
    const allTimes = processedData.flatMap(d => [
        d.queue_time, 
        d.start_time, 
        d.end_time, 
        ...(d.history || []).flatMap(h => [h.start_time, h.end_time])
    ]).filter(t => t != null);
    
    if (allTimes.length === 0) return { minTime: Date.now() / 1000, maxTime: Date.now() / 1000 + 60 };
    const min = Math.min(...allTimes);
    const max = Math.max(...allTimes);
    const padding = (max - min) * 0.05 || 10;
    return { minTime: min, maxTime: max + padding };
  }, [processedData]);

  const scale = useMemo(() => {
    const duration = maxTime - minTime;
    if (duration <= 0) return () => 0;
    return (time) => ((time - minTime) / duration) * 100;
  }, [minTime, maxTime]);

  const formatSecondsToMMSS = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };
  
  const calculateNiceTimeMarkers = (min, max, targetCount = 5) => {
      const duration = max - min;
      if (duration <= 0) return [];
      const niceIntervals = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600 , 4000 , 4500 , 5000];
      const rawStep = duration / targetCount;
      const niceStep = niceIntervals.find(interval => interval >= rawStep) || niceIntervals[niceIntervals.length -1];

      const markers = [];
      for (let i = 0; (i * niceStep) <= duration * 1.05; i++) {
          const elapsedTime = i * niceStep;
          markers.push({
              time: min + elapsedTime,
              label: formatSecondsToMMSS(elapsedTime),
              position: scale(min + elapsedTime),
          });
      }
      return markers;
  };

  const timeMarkers = useMemo(() => {
    return calculateNiceTimeMarkers(minTime, maxTime);
  }, [minTime, maxTime, scale]);

  const handleMouseEnter = (data, position) => setHoveredData(data);
  const handleMouseLeave = () => setHoveredData(null);
  const handleMouseMove = (e) => {
    if (hoveredData) {
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    }
  };
  
  useEffect(() => {
    const currentChartRef = chartRef.current;
    currentChartRef?.addEventListener('mousemove', handleMouseMove);
    return () => currentChartRef?.removeEventListener('mousemove', handleMouseMove);
  }, [hoveredData]);

  const handleTaskClick = (taskKey) => { setSelectedTaskKey(prev => (prev === taskKey ? null : taskKey)); };
  const handleBackgroundClick = () => { setSelectedTaskKey(null); };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8" onClick={handleBackgroundClick}>
        <div className='flex flex-row gap-4 justify-end mb-4'>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-blue-500"></div>
              <span className="text-sm font-medium text-gray-700">Executing</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-blue-300"></div>
              <span className="text-sm font-medium text-gray-700">Queued</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-red-400"></div>
              <span className="text-sm font-medium text-gray-700">Expired</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-gray-400"></div>
              <span className="text-sm font-medium text-gray-700">Not Ready</span>
            </div>
        </div>
        <div className='px-3 pt-8' ref={chartRef}>
          <div className="relative">
            <div className="absolute ml-48 inset-0 z-0">
              {timeMarkers.map(marker => (
                <div key={marker.time} className="absolute h-full" style={{ left: `${marker.position}%` }}>
                  <span className="absolute -top-6 transform -translate-x-1/2 text-xs font-medium text-gray-600">{marker.label}</span>
                  <div className="w-px h-full bg-gray-200"></div>
                </div>
              ))}
            </div>
            <div className="space-y-4 relative z-10">
              {processedData.map((task) => {
                const isAnyTaskSelected = selectedTaskKey !== null;
                const isHighlighted = highlightedDependencies.has(task.key);
                const rowClasses = `flex items-center group transition-opacity duration-300 cursor-pointer ${isAnyTaskSelected && !isHighlighted ? "opacity-20" : "opacity-100"}`;
                let nameBoxStyle = 'bg-gray-50 border-gray-200';
                const Duration = (task.end_time - task.queue_time).toFixed(2);
                if (task.end_time && !task.expired) {
                  if (Duration > task.runtime) {
                    nameBoxStyle = 'bg-red-100 border-red-300';
                  } else {
                    nameBoxStyle = 'bg-green-100 border-green-300';
                  }
                }
                return (
                  <div key={task.key} className={rowClasses} onClick={(e) => { e.stopPropagation(); handleTaskClick(task.key); }}>
                    <div className="w-48 flex-shrink-0 pr-4">
                      <div className={`rounded-lg p-3 border ${nameBoxStyle} ${isHighlighted ? 'border-blue-500 ring-2 ring-blue-300' : ''} group-hover:border-blue-300 transition-all`}>
                        <div className="text-xs font-semibold truncate" title={task.name || task.key}>{task.name || task.key}</div>
                        <div className="text-xs mt-1">Runtime: {Duration}/{task.runtime}s</div>
                      </div>
                    </div>
                    <div className="flex-1 h-12 flex items-center">
                      <TaskBar taskKey={task.key} taskData={task} scale={scale} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Tooltip data={hoveredData} position={tooltipPosition} />
    </>
  );
};